/**
 * snd-locale-popup.js — SND-11167 — Variant 1 "Clean Card"
 * Session-gated, mismatch-triggered language/country selector popup, with
 * styled custom dropdowns that replace the native OS <select> list.
 *
 * Lightweight: dependency-free module (~2KB gzipped), deferred, fires in
 * requestIdleCallback, no layout shift. The custom dropdowns are built LAZILY —
 * only when the dialog actually opens — so the ~95% of visitors who never see
 * the popup pay nothing beyond a cheap idle-time language check.
 *
 * Shows ONLY to geo-mismatched visitors (SND-14188): /browsing_context_suggestions
 * gives the IP-detected country; when that country's preferred language is the one
 * already served (e.g. LT visitor on soundium.lt), the popup stays silent no matter
 * what the browser language says. Recommendation priority for the rest:
 *   1. First browser language (navigator.languages) that is published AND
 *      differs from the language currently served.
 *   2. Fallback: the geo-detected country's preferred published language.
 *
 * Shows at most once (cookie `snd_locale_prompt_seen`, 180d, on save OR dismiss).
 * Bot-safe: deferred module, <dialog> closed by default & outside #MainContent,
 * no server-side / Accept-Language redirect. In the Theme editor it opens for
 * preview when its section is selected and closes when you select another
 * section, and it never writes the cookie.
 *
 * The native <select> elements stay as the source of truth for form submission
 * (graceful no-JS fallback); the custom dropdown mirrors them.
 */
const SEEN_COOKIE = 'snd_locale_prompt_seen';

const COUNTRY_LANG = {
  LT: 'lt', LV: 'lv', EE: 'et', FI: 'fi', SE: 'sv', DK: 'da', NO: 'nb',
  PL: 'pl', DE: 'de', AT: 'de', CH: 'de', FR: 'fr', BE: 'fr', LU: 'fr',
  ES: 'es', IT: 'it', NL: 'nl', PT: 'pt-PT', GR: 'el', CZ: 'cs',
  HU: 'hu', RO: 'ro', SK: 'sk', BG: 'bg', HR: 'hr',
};

const CARET_SVG =
  '<svg viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M1 1.5 6 6.5 11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

// ISO-3166 alpha-2 -> flag emoji (regional indicator symbols).
function flagEmoji(iso) {
  if (!/^[A-Za-z]{2}$/.test(iso)) return '';
  return String.fromCodePoint(
    ...[...iso.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)
  );
}

class SndLocalePopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.langSelect = this.querySelector('select[name="language_code"]');
    if (!this.dialog) return;

    this.current = (this.dataset.currentLanguage || '').toLowerCase();
    this.country = (this.dataset.currentCountry || '').toUpperCase();
    this.available = (this.dataset.availableLanguages || '')
      .toLowerCase()
      .split(',')
      .filter(Boolean);
    this.designMode = !!(window.Shopify && window.Shopify.designMode);
    this.dropdowns = [];
    this._enhanced = false;

    // Theme editor: open only while THIS section is selected, close on deselect,
    // so the merchant can preview/style it but is not stuck with it on screen
    // while editing other sections.
    if (this.designMode) {
      this.sectionEl = this.closest('.shopify-section');
      const isMine = (e) =>
        this.sectionEl && e.detail && this.sectionEl.id === 'shopify-section-' + e.detail.sectionId;
      document.addEventListener('shopify:section:select', (e) => {
        if (isMine(e)) this.open();
      });
      document.addEventListener('shopify:section:deselect', (e) => {
        if (isMine(e) && this.dialog.open) this.dialog.close();
      });
    }

    this.querySelectorAll('[data-snd-close]').forEach((el) =>
      el.addEventListener('click', () => this.dismiss())
    );
    this.dialog.addEventListener('cancel', () => this.dismiss());

    const form = this.querySelector('form');
    if (form) form.addEventListener('submit', () => this.setSeen());

    // Never block first paint / LCP.
    const trigger = () => this.maybeShow();
    if ('requestIdleCallback' in window) {
      requestIdleCallback(trigger, { timeout: 2000 });
    } else {
      window.addEventListener('load', trigger);
    }
  }

  /* ---------- show / recommendation logic ---------- */

  async maybeShow() {
    // In the editor the popup is driven by section select/deselect (wired in
    // connectedCallback), not auto-opened on load — so it doesn't sit on screen
    // while you work on other sections.
    if (this.designMode) return;
    if (this.hasSeen()) return;

    const rec = this.recommended();
    if (!rec || rec === this.current) return;

    // Geo gate (SND-14188): a visitor who already reads the page in their own
    // country's language is in the right place — a browser-language mismatch
    // alone (very common: LT visitors with EN browsers on soundium.lt) is not
    // a reason to interrupt. Only geo-mismatched visitors get the prompt.
    // On lookup failure stay quiet rather than risk a false positive.
    const geo = await this.detectedCountry();
    if (!geo) return;
    const geoLang = (COUNTRY_LANG[geo] || '').toLowerCase();
    if (geoLang === this.current) return;

    // Set the native select first; enhancement (on open) reads from it.
    this.setSelectValue(this.langSelect, this.matchOption(rec));
    this.open();
  }

  detectedCountry() {
    try {
      const cached = sessionStorage.getItem('snd_geo_country');
      if (cached) return Promise.resolve(cached);
    } catch (e) {
      /* sessionStorage unavailable — fall through to fetch */
    }
    return fetch('/browsing_context_suggestions', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const iso = (
          (d && d.detected_values && d.detected_values.country && d.detected_values.country.handle) || ''
        ).toUpperCase();
        if (!iso) return null;
        try {
          sessionStorage.setItem('snd_geo_country', iso);
        } catch (e) {
          /* ignore */
        }
        return iso;
      })
      .catch(() => null);
  }

  recommended() {
    const langs =
      navigator.languages && navigator.languages.length
        ? navigator.languages
        : [navigator.language];

    for (const raw of langs.filter(Boolean)) {
      const l = raw.toLowerCase();
      const cand = this.available.includes(l)
        ? l
        : this.available.includes(l.split('-')[0])
        ? l.split('-')[0]
        : null;
      if (cand && cand !== this.current) return cand;
    }

    const byCountry = (COUNTRY_LANG[this.country] || '').toLowerCase();
    if (byCountry && byCountry !== this.current && this.available.includes(byCountry)) {
      return byCountry;
    }

    return null;
  }

  matchOption(rec) {
    if (!this.langSelect) return null;
    const opt = Array.from(this.langSelect.options).find(
      (o) => o.value.toLowerCase() === rec
    );
    return opt ? opt.value : this.langSelect.value;
  }

  /* ---------- custom dropdowns (built lazily on first open) ---------- */

  ensureEnhanced() {
    if (this._enhanced) return;
    this._enhanced = true;
    this.querySelectorAll('.snd-locale-popup__native').forEach((select) =>
      this.enhanceSelect(select)
    );
  }

  enhanceSelect(select) {
    const field = select.closest('.snd-locale-popup__field') || select.parentElement;
    const withFlag = select.hasAttribute('data-snd-flag');

    const dd = document.createElement('div');
    dd.className = 'snd-dd';
    dd.setAttribute('data-open', 'false');

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'snd-dd__trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    const labelEl = field.querySelector('.snd-locale-popup__label');
    if (labelEl) trigger.setAttribute('aria-label', labelEl.textContent.trim());

    const value = document.createElement('span');
    value.className = 'snd-dd__value';

    const caret = document.createElement('span');
    caret.className = 'snd-dd__caret';
    caret.innerHTML = CARET_SVG;

    trigger.append(value, caret);

    const list = document.createElement('ul');
    list.className = 'snd-dd__list';
    list.setAttribute('role', 'listbox');
    list.hidden = true;

    const label = (opt) =>
      withFlag ? `${flagEmoji(opt.value)} ${opt.textContent.trim()}` : opt.textContent.trim();

    Array.from(select.options).forEach((opt) => {
      const li = document.createElement('li');
      li.className = 'snd-dd__option';
      li.setAttribute('role', 'option');
      li.dataset.value = opt.value;
      li.textContent = label(opt);
      li.setAttribute('aria-selected', opt.value === select.value ? 'true' : 'false');
      li.addEventListener('click', () => {
        this.setSelectValue(select, opt.value);
        this.closeDropdown(dd);
        trigger.focus();
      });
      list.appendChild(li);
    });

    const syncTrigger = () => {
      const opt = select.options[select.selectedIndex];
      value.textContent = opt ? label(opt) : '';
      list.querySelectorAll('.snd-dd__option').forEach((li) =>
        li.setAttribute('aria-selected', li.dataset.value === select.value ? 'true' : 'false')
      );
    };
    syncTrigger();
    select.addEventListener('change', syncTrigger);

    trigger.addEventListener('click', () => this.toggleDropdown(dd));
    trigger.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.openDropdown(dd);
      }
    });
    list.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeDropdown(dd);
        trigger.focus();
      }
    });

    dd.append(trigger, list);
    field.appendChild(dd);
    field.classList.add('is-enhanced');

    this.dropdowns.push(dd);
  }

  setSelectValue(select, val) {
    if (!select || val == null) return;
    select.value = val;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  toggleDropdown(dd) {
    dd.dataset.open === 'true' ? this.closeDropdown(dd) : this.openDropdown(dd);
  }

  openDropdown(dd) {
    this.dropdowns.forEach((other) => other !== dd && this.closeDropdown(other));
    dd.dataset.open = 'true';
    dd.querySelector('.snd-dd__trigger').setAttribute('aria-expanded', 'true');
    const list = dd.querySelector('.snd-dd__list');
    list.hidden = false;
    const selected = list.querySelector('[aria-selected="true"]');
    if (selected) selected.scrollIntoView({ block: 'nearest' });
  }

  closeDropdown(dd) {
    dd.dataset.open = 'false';
    dd.querySelector('.snd-dd__trigger').setAttribute('aria-expanded', 'false');
    dd.querySelector('.snd-dd__list').hidden = true;
  }

  /* ---------- dialog open/close ---------- */

  open() {
    this.ensureEnhanced();
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
    if (!this._outsideBound) {
      this._outsideBound = true;
      this.dialog.addEventListener('click', (e) => {
        if (!e.target.closest('.snd-dd')) {
          this.dropdowns.forEach((dd) => this.closeDropdown(dd));
        }
      });
    }
  }

  dismiss() {
    this.setSeen();
    if (this.dialog.open) this.dialog.close();
  }

  /* ---------- once-only cookie ---------- */

  hasSeen() {
    return document.cookie.split('; ').some((c) => c.startsWith(SEEN_COOKIE + '='));
  }

  setSeen() {
    if (this.designMode) return;
    document.cookie = SEEN_COOKIE + '=1; max-age=15552000; path=/; samesite=lax';
  }
}

if (!customElements.get('snd-locale-popup')) {
  customElements.define('snd-locale-popup', SndLocalePopup);
}


